import React, { useCallback, useState, useMemo, useRef } from 'react';
import { SaveButton, useNotify, useRefresh } from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles } from '@material-ui/core/styles';
import { omit } from 'lodash';

import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import UserNotificationApi from '../../../core/apis/UserNotificationApi';
import UserNotificationForm from './UserNotificationForm';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { buildId } from './UserNotificationCommon';

const useStyles = makeStyles({
  errorAlreadyExists: {
    marginTop: 0
  }
});

const UserNotificationCreate = props => {
  const {
    operation,
    showDialog,
    popupTitle,
    okButtonLabel,
    handleDialogToggle,
    notifications = [],
    id: userId
  } = props;

  const notificationForm = useRef();
  const [notificationFormValues, setNotificationFormValues] = useState();
  const [isCreating, setIsCreating] = useState(false);
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const refresh = useRefresh();
  const classes = useStyles();

  const handleSaveClick = useCallback(event => {
    // Trigger a submit of our custom quick create/update form
    // This is needed because our modal action buttons are oustide the form
    notificationForm.current && notificationForm.current.submit(event);
  }, []);

  const handleClose = useCallback(() => {
    handleDialogToggle();
  }, [handleDialogToggle]);

  const handleSubmit = useCallback(
    value => {
      const notificationArray = notifications.map(notification =>
        omit(notification, ['selected', 'id'])
      );

      if (value.deliveryMethod === 'EMAIL') {
        notificationArray.push({ ...value, parameter: '' });
      } else {
        notificationArray.push({ ...value });
      }

      setIsCreating(true);

      UserNotificationApi.update(notificationArray, userId)
        .then(() => {
          notify('ra.notification.created');
          setIsCreating(false);
          handleDialogToggle();
          refresh();
        })
        .catch(e => {
          setIsCreating(false);
          notifyServerError(e);
        });
    },
    [
      handleDialogToggle,
      notifications,
      notify,
      refresh,
      userId,
      notifyServerError
    ]
  );

  const alreadyExists = useMemo(() => {
    if (!notificationFormValues) return false;

    const id = buildId(notificationFormValues);
    return notifications.some(item => item.id === id);
  }, [notifications, notificationFormValues]);

  return (
    <Dialog
      fullWidth
      open={!!showDialog}
      aria-label={`${operation} user notifications`}
      maxWidth="sm"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
    >
      <DialogTitleWithCloseButton onClose={handleClose}>
        {popupTitle}
      </DialogTitleWithCloseButton>
      <DialogContent>
        <React.Fragment>
          <UserNotificationForm
            {...props}
            notificationFormRef={notificationForm}
            notificationFormValues={notificationFormValues}
            setNotificationFormValues={setNotificationFormValues}
            handleSubmit={handleSubmit}
          />
          {alreadyExists && (
            <FormHelperText
              error
              classes={{ root: classes.errorAlreadyExists }}
            >
              Notification preference already defined
            </FormHelperText>
          )}
        </React.Fragment>
      </DialogContent>
      <DialogActions>
        <SaveButton
          style={{ minWidth: '82px' }}
          saving={isCreating}
          handleSubmitWithRedirect={handleSaveClick}
          label={okButtonLabel}
          icon={<></>}
          redirect={false}
          disabled={alreadyExists}
          id="create"
        />
        <Button
          onClick={handleClose}
          variant="outlined"
          color="primary"
          id="cancel"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserNotificationCreate;
